import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Send, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InviteGuestsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const purchaseId = searchParams.get('purchase');
  
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [guests, setGuests] = useState([{ phone: '', email: '' }]);
  const [sessionId, setSessionId] = useState('');
  const [invitesSent, setInvitesSent] = useState(false);

  useEffect(() => {
    fetchPurchase();
  }, [purchaseId]);

  const fetchPurchase = async () => {
    try {
      const response = await axios.get(`${API}/purchases/my-tours`);
      const currentPurchase = response.data.find(p => p.purchase_id === purchaseId);
      
      if (!currentPurchase) {
        toast.error('Purchase not found');
        navigate('/my-tours');
        return;
      }
      
      setPurchase(currentPurchase);
      
      // Initialize guest slots based on additional devices
      const maxGuests = currentPurchase.additional_devices;
      setGuests(Array(Math.min(maxGuests, 10)).fill({ phone: '', email: '' }));
    } catch (error) {
      toast.error('Failed to load purchase');
    } finally {
      setLoading(false);
    }
  };

  const updateGuest = (index, field, value) => {
    const newGuests = [...guests];
    newGuests[index] = { ...newGuests[index], [field]: value };
    setGuests(newGuests);
  };

  const sendInvites = async () => {
    // Validate at least one contact method per guest
    const validGuests = guests.filter(g => g.phone || g.email);
    
    if (validGuests.length === 0) {
      toast.error('Please add at least one guest phone or email');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/group/invite`, {
        purchase_id: purchaseId,
        guests: validGuests
      });

      setSessionId(response.data.session_id);
      setInvitesSent(true);
      toast.success(`Invites sent to ${validGuests.length} guest(s)!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  const skipInvites = () => {
    navigate(`/tour-player/${purchaseId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (invitesSent) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white border-4 border-green-500 rounded-2xl shadow-card p-8 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-black uppercase text-gray-900 mb-4">Invites Sent!</h2>
            <p className="text-lg text-gray-700 mb-6">
              Your guests will receive a text/email with a link to join your tour. They just need to enter their name - no app download required!
            </p>
            <button
              onClick={() => navigate(`/tour-player/${purchaseId}?host=true`)}
              data-testid="start-tour-button"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-full font-black text-xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95"
            >
              Start Tour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      <header className="bg-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/my-tours')}
            data-testid="back-button"
            className="flex items-center text-white hover:text-cyan-100 mb-4 transition-colors font-bold uppercase tracking-wide"
          >
            <ArrowLeft className="w-6 h-6 mr-2" />
            Back
          </button>
          <div className="flex items-center">
            <Users className="w-12 h-12 mr-4" />
            <div>
              <h1 className="text-4xl font-black uppercase">Invite Guests</h1>
              <p className="text-cyan-100 text-lg mt-1">Share your tour with friends</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black uppercase text-gray-900">Your Tour</h2>
            <span className="bg-cyan-500 text-white px-4 py-2 rounded-full font-bold">
              {purchase?.additional_devices || 0} Guest Spots
            </span>
          </div>
          <p className="text-lg text-gray-700 mb-2">
            <strong>{purchase?.tour?.title}</strong>
          </p>
          <p className="text-gray-600">You can invite up to {purchase?.additional_devices || 0} guests</p>
        </div>

        <div className="bg-white border-4 border-blue-500 rounded-2xl shadow-card p-8 mb-6">
          <h3 className="text-2xl font-black uppercase text-gray-900 mb-4">How It Works</h3>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start">
              <span className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
              <p>Enter your guests' phone numbers or email addresses below</p>
            </div>
            <div className="flex items-start">
              <span className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
              <p>They'll receive a text/email with a link</p>
            </div>
            <div className="flex items-start">
              <span className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
              <p>They click the link and enter their name (that's it!)</p>
            </div>
            <div className="flex items-start">
              <span className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
              <p>They're automatically added to your tour - no app download needed</p>
            </div>
          </div>
        </div>

        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8 mb-6">
          <h3 className="text-2xl font-black uppercase text-gray-900 mb-6">Guest Contact Info</h3>
          <div className="space-y-4">
            {guests.map((guest, index) => (
              <div key={index} className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Guest {index + 1} - Phone
                  </label>
                  <input
                    type="tel"
                    value={guest.phone}
                    onChange={(e) => updateGuest(index, 'phone', e.target.value)}
                    data-testid={`guest-phone-${index}`}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Guest {index + 1} - Email
                  </label>
                  <input
                    type="email"
                    value={guest.email}
                    onChange={(e) => updateGuest(index, 'email', e.target.value)}
                    data-testid={`guest-email-${index}`}
                    placeholder="friend@example.com"
                    className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all"
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4 bg-blue-50 p-4 rounded-lg">
            💡 <strong>Tip:</strong> You can provide just phone OR just email - whichever is easiest
          </p>
        </div>

        <div className="flex gap-4">
          <button
            onClick={skipInvites}
            data-testid="skip-button"
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-4 rounded-full font-bold text-lg transition-all active:scale-95"
          >
            Skip for Now
          </button>
          <button
            onClick={sendInvites}
            disabled={sending}
            data-testid="send-invites-button"
            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-4 rounded-full font-black text-lg shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Send className="w-5 h-5 mr-2" />
            {sending ? 'Sending...' : 'Send Invites'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default InviteGuestsPage;